import { alignment } from "../models/alignment.enum";
import { columnType } from "./models/column-type.enum";
import { headType } from "./models/head-type.enum";
import { HeadType, headTypesModel } from "./models/head-types.model";
import { BaseBabylonService } from "../base-babylon.service";
import { bodiesEnum } from "./models/bodies.enum";
import { PartTransformation } from "../models/part-transformation";
import { coreType } from "./models/core-type.enum";
import { coreTypesModel } from "./models/core-types.model";
import { BuildingHelperService } from "../../../app-confi/services/building-helper.service";
import { SceneManager } from "../../../common/scene.manager";
import { config } from "../../../app-confi/app-config";
import { partTypeEnum } from "./models/part-type.enum";
import { accuracyEnum } from "../../../common/models/accuracy.enum";
import { TowerColumn, TowerPart } from "./models/props-config";
import { segmentType } from "./models/segment-type";
import { buildingType } from "../models/building-type.enum";
import { Point3 } from "../../../common/models/point.type";
import { columnDefs, columnSubtype } from "./models/column-definition";

export class TowerBabylonService extends BaseBabylonService {

    private bhs: BuildingHelperService;

    constructor(
        sceneManager: SceneManager,
        buildingHelperService: BuildingHelperService,
    ) {
        super(sceneManager);
        this.bhs = buildingHelperService;
    }

    isMoosPlazaColumn(type: columnType | headType) {
        return columnDefs.find(c => c.type === type).columnSubtypes.includes(columnSubtype.moosplaza)
    }

    renderColumnsTo3D(
        buildingId: string | number,
        partsIn: TowerPart[],
        locationInSite,
        selectedCoreType: coreType,
        accuracy: accuracyEnum
    ): Promise<any> {

        const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.tower);
        const promises = [];

        //#region Head Left

        const partHeadLeft = partsIn.find(p => p.type === partTypeEnum.headLeft);
        const headLeftCol = partHeadLeft.columns[0];
        const headLeftColPlinthSegment = this.bhs.getColumnSegment(headLeftCol, segmentType.plinth);
        if (headLeftColPlinthSegment) {
            if (headLeftColPlinthSegment.custom) {
                const headTypeModel = headTypesModel.find(t => t.type === headLeftCol.type);

                const locationInBuilding: Point3 = [
                    headTypeModel.width / 2,
                    headLeftColPlinthSegment.customSegmentHeight / 2,
                    -headTypeModel.length / 2,
                ];
                this.sceneManager.modelLoaderService.createCustomBoxMesh(
                    buildingId,
                    headTypeModel.width,
                    headTypeModel.length,
                    headLeftColPlinthSegment.customSegmentHeight,
                    { groupId: buildingId, subgroupId: headLeftCol.id, type: headLeftColPlinthSegment.sectionTypes[0], props: {} },
                    locationInBuilding,
                    locationInSite,
                    partHeadLeft.partTransformation);
            }

            const renderPromisesHeadLeft = this.renderInstances(buildingId, headLeftCol.id, headLeftCol.unitsAndAmenities, locationInSite, partHeadLeft.partTransformation, accuracy, floorHeight);
            promises.push(...renderPromisesHeadLeft);
        }
        //#endregion

        //#region Body A
        const partBodyA = partsIn.find(p => p.type === partTypeEnum.bodyA);
        const bodyAColumns = partBodyA.columns;

        let s;
        if (headLeftCol) {
            s = headTypesModel.find(s => s.type === headLeftCol.type);
        } else {
            s = {
                coreDiffBays: 0,
                width: 0,
                length: 0,
                orientationConfig: {
                    shift: 0,
                    corridorCornerDistance: 0,
                },
            } as any;
        }

        let offsetBasedOnColumn = s.width;

        bodyAColumns.forEach(column => {
            const columnPlinthSegment = this.bhs.getColumnSegment(column, segmentType.plinth);
            if (columnPlinthSegment.custom) {
                const width = column.bays * config.towerBaySize;
                const locationInBuilding: Point3 = [
                    offsetBasedOnColumn + width / 2,
                    columnPlinthSegment.customSegmentHeight / 2,
                    -columnPlinthSegment.customSegmentWidth / 2,
                ];
                this.sceneManager.modelLoaderService.createCustomBoxMesh(
                    buildingId,
                    width,
                    columnPlinthSegment.customSegmentWidth,
                    columnPlinthSegment.customSegmentHeight,
                    { groupId: buildingId, subgroupId: column.id, type: columnPlinthSegment.sectionTypes[0], props: {} },
                    locationInBuilding,
                    locationInSite,
                    partBodyA.partTransformation);
            }
            offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.towerBaySize;
            const renderPromises = this.renderInstances(buildingId, column.id, column.unitsAndAmenities, locationInSite, partBodyA.partTransformation, accuracy, floorHeight);
            promises.push(...renderPromises);
        })

        //#endregion

        //#region Core

        const partCore = partsIn.find(p => p.type === partTypeEnum.core);
        const coreColumn = partCore.columns[0];

        const renderPromises = this.renderInstances(buildingId, coreColumn.id, coreColumn.unitsAndAmenities, locationInSite, partCore.partTransformation, accuracy, floorHeight);
        promises.push(...renderPromises);

        //#endregion

        //#region Body B
        const partBodyB = partsIn.find(p => p.type === partTypeEnum.bodyB);
        const bodyBColumns = partBodyB.columns;

        offsetBasedOnColumn = s.width;

        bodyBColumns.forEach(column => {
            const columnPlinthSegment = this.bhs.getColumnSegment(column, segmentType.plinth);
            if (columnPlinthSegment.custom) {
                const width = column.bays * config.towerBaySize;
                const locationInBuilding: Point3 = [
                    offsetBasedOnColumn + width / 2,
                    columnPlinthSegment.customSegmentHeight / 2,
                    -columnPlinthSegment.customSegmentWidth / 2,
                ];
                this.sceneManager.modelLoaderService.createCustomBoxMesh(
                    buildingId,
                    width,
                    columnPlinthSegment.customSegmentWidth,
                    columnPlinthSegment.customSegmentHeight,
                    { groupId: buildingId, subgroupId: column.id, type: columnPlinthSegment.sectionTypes[0], props: {} },
                    locationInBuilding,
                    locationInSite,
                    partBodyB.partTransformation);
            }
            offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.towerBaySize;
            const renderPromises = this.renderInstances(buildingId, column.id, column.unitsAndAmenities, locationInSite, partBodyB.partTransformation, accuracy, floorHeight);
            promises.push(...renderPromises);
        })

        //#endregion

        //#region Head Right

        const partHeadRight = partsIn.find(p => p.type === partTypeEnum.headRight);
        const headRightCol = partHeadRight.columns[0];

        let hr;
        if (headRightCol) {
            hr = headTypesModel.find(s => s.type === headRightCol.type);
        } else {
            hr = {
                coreDiffBays: 0,
                width: 0,
                length: 0,
                orientationConfig: {
                    shift: 0,
                    corridorCornerDistance: 0,
                },
            } as any;
        }

        const core = coreTypesModel.find(core => core.type === selectedCoreType);

        const length = (core.bays - s.coreDiffBays) * config.towerBaySize;
        offsetBasedOnColumn = s.width + length - hr.orientationConfig.shift;

        const headRightColPlinthSegment = this.bhs.getColumnSegment(headRightCol, segmentType.plinth);
        if (headRightColPlinthSegment) {
            if (headRightColPlinthSegment.custom) {

                const locationInBuilding: Point3 = [
                    offsetBasedOnColumn + hr.width / 2,
                    headRightColPlinthSegment.customSegmentHeight / 2,
                    -hr.length / 2,
                ];

                this.sceneManager.modelLoaderService.createCustomBoxMesh(
                    buildingId,
                    hr.width,
                    hr.length,
                    headRightColPlinthSegment.customSegmentHeight,
                    { groupId: buildingId, subgroupId: headRightCol.id, type: headRightColPlinthSegment.sectionTypes[0], props: {} },
                    locationInBuilding,
                    locationInSite,
                    partHeadRight.partTransformation);
            }

            const renderPromisesHeadRight = this.renderInstances(buildingId, headRightCol.id, headRightCol.unitsAndAmenities, locationInSite, partHeadRight.partTransformation, accuracy, floorHeight);
            promises.push(...renderPromisesHeadRight);
        }
        //#endregion
        return Promise.all(promises);
    }

    mapPartsTo3D(
        partsIn: TowerPart[],
        moosPlazaAlignment: alignment,
        moosPlazaPosition: bodiesEnum,
        doNotOrder?: boolean,
    ): TowerPart[] {
        let result;
        const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.tower);

        // make copy, dont manipulate existing columns

        let bodyAColumns = [...partsIn.find(p => p.type === partTypeEnum.bodyA).columns.map(c => ({ ...c }))];
        let bodyBColumns = [...partsIn.find(p => p.type === partTypeEnum.bodyB).columns.map(c => ({ ...c }))];

        const headLeftColumn = partsIn.find(p => p.type === partTypeEnum.headLeft).columns[0];
        const headRightColumn = partsIn.find(p => p.type === partTypeEnum.headRight).columns[0];

        const core = partsIn.find(p => p.type === partTypeEnum.core).columns[0];

        // load not in chunks of columns, but first load left head,
        // then columns on body A in a group with one transf,
        // then body B with another global transform,
        // then head right

        let bodyA;
        let bodyB;
        if (doNotOrder) {
            bodyA = bodyAColumns;
            bodyB = bodyBColumns;
        } else {
            bodyA = this.orderColumns(moosPlazaAlignment, moosPlazaPosition, bodyAColumns);
            bodyB = this.orderColumns(moosPlazaAlignment, moosPlazaPosition, bodyBColumns);
        }

        //#region HEAD LEFT

        let headLeft = headTypesModel.find(s => s.type === headLeftColumn?.type);
        if (!headLeft) {
            headLeft = {
                coreDiffBays: 0,
                width: 0,
                orientationConfig: {
                    shift: 0,
                    corridorCornerDistance: 0,
                },
            } as any;
        }

        let partTransformation: PartTransformation = {
            id: partTypeEnum.headLeft,
            position: [0, 0, headLeft.orientationConfig.corridorCornerDistance],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
        };

        let offsetBasedOnColumn = 0;

        const headLeftPart: TowerPart = {
            type: partTypeEnum.headLeft,
            columns: [],
            partTransformation
        }

        if (headLeftColumn) {
            offsetBasedOnColumn = this.getUnitAndAmenityDataForColumn(headLeftColumn, offsetBasedOnColumn, floorHeight);
            headLeftPart.columns.push(headLeftColumn);
        }

        //#endregion
        //#region CORE TOWER


        let coreCorridor;
        if (core) {
            coreCorridor = coreTypesModel.find(s => s.type === core.type as string);
        } else {
            coreCorridor = { corridorWidth: 0, bays: 0 }
        }

        let partTransformationCoreTower: PartTransformation = {
            id: partTypeEnum.core,
            position: [headLeft.width - headLeft.orientationConfig.shift, 0, coreCorridor.corridorWidth / 2],
            rotation: [0, 0, 0],
            scale: [1, 1, 1]
        };

        const corePart: TowerPart = {
            type: partTypeEnum.core,
            columns: [],
            partTransformation: partTransformationCoreTower
        }

        this.getUnitAndAmenityDataForColumn(core, 0, floorHeight);

        corePart.columns.push(core);

        //#endregion
        //#region BODY A

        let partTransformationA: PartTransformation = {
            id: partTypeEnum.bodyA,
            position: [-headLeft.orientationConfig.shift, 0, -coreCorridor.corridorWidth / 2],
            rotation: [0, 0, 0],
            scale: [1, 1, 1]
        };

        const bodyAPart: TowerPart = {
            type: partTypeEnum.bodyA,
            columns: [],
            partTransformation: partTransformationA
        }

        offsetBasedOnColumn = this.layDownBodyColumns(offsetBasedOnColumn, headLeft, bodyA);

        bodyAPart.columns.push(...bodyA);

        //#endregion
        //#region BODY B

        const length = (coreCorridor.bays - headLeft.coreDiffBays) * config.towerBaySize;
        offsetBasedOnColumn = headLeft.width;

        let partTransformationB: PartTransformation = {
            id: partTypeEnum.bodyB,
            position: [length + (offsetBasedOnColumn * 2), 0, coreCorridor.corridorWidth / 2],
            rotation: [0, Math.PI, 0],
            scale: [1, 1, 1]
        };

        const bodyBPart: TowerPart = {
            type: partTypeEnum.bodyB,
            columns: [],
            partTransformation: partTransformationB
        }

        offsetBasedOnColumn = this.layDownBodyColumns(offsetBasedOnColumn, headLeft, bodyB);

        bodyBPart.columns.push(...bodyB);

        //#endregion
        //#region HEAD RIGHT
        let headRight = headTypesModel.find(s => s.type === headRightColumn?.type);
        if (!headRight) {
            headRight = {
                coreDiffBays: 0,
                width: 0,
                orientationConfig: {
                    shift: 0,
                    corridorCornerDistance: 0,
                },
            } as any;
        }

        let partTransformationR: PartTransformation = {
            id: partTypeEnum.headRight,
            position: [0, 0, headRight.orientationConfig.corridorCornerDistance],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
        };

        const headRightPart: TowerPart = {
            type: partTypeEnum.headRight,
            columns: [],
            partTransformation: partTransformationR
        }

        if (headRightColumn) {
            offsetBasedOnColumn = headLeft.width + length;
            offsetBasedOnColumn = this.getUnitAndAmenityDataForColumn(headRightColumn, offsetBasedOnColumn, floorHeight);
            headRightPart.columns.push(headRightColumn);
        }
        //#endregion

        result = [headLeftPart, corePart, bodyAPart, bodyBPart, headRightPart];
        return result;
    }

    private layDownBodyColumns(
        offsetBasedOnColumn: number,
        s: HeadType,
        body: TowerColumn[],
    ) {
        offsetBasedOnColumn = s.width;
        const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.tower);

        body.forEach(column => {
            offsetBasedOnColumn = this.getUnitAndAmenityDataForColumn(column, offsetBasedOnColumn, floorHeight)
        });

        return offsetBasedOnColumn;
    }

    private orderColumns(moosPlazaAlignment: alignment, moosPlazaPosition: bodiesEnum, columns: TowerColumn[]) {
        const columnsInOrder: TowerColumn[] = [];

        // add left head or moos plein column as first thing in the list
        const moosPlaza = columns.find(column => this.isMoosPlazaColumn(column.type));
        const allCoreColumns = []

        if (moosPlaza && moosPlazaAlignment === alignment.left && moosPlazaPosition === bodiesEnum.A) {
            columnsInOrder.push(moosPlaza);
        } else if (moosPlaza && moosPlazaAlignment === alignment.right && moosPlazaPosition === bodiesEnum.B) {
            columnsInOrder.push(moosPlaza);
        }

        // add all in betweens
        const allExceptHeadsStaircasesAndMoosPlein = columns.filter(c => c.type === columnType.C_XS || c.type === columnType.C_M || c.type === columnType.C_S1S2 || c.type === columnType.C_XL);

        const shuffledColumns = this.equalizeColumns(allExceptHeadsStaircasesAndMoosPlein);
        const totalMiddleBays = shuffledColumns.map(s => s.bays).reduce((p, c) => p + c, 0);
        const orderedWithStaircases = this.bhs.orderStaircasesInBody<TowerColumn>(moosPlazaAlignment, allCoreColumns, totalMiddleBays, shuffledColumns, columns, this.isMoosPlazaColumn);

        columnsInOrder.push(...orderedWithStaircases);

        // as a last lets get the right column
        if (moosPlaza && moosPlazaAlignment === alignment.right && moosPlazaPosition === bodiesEnum.A) {
            columnsInOrder.push(moosPlaza);
        } else if (moosPlaza && moosPlazaAlignment === alignment.left && moosPlazaPosition === bodiesEnum.B) {
            columnsInOrder.push(moosPlaza);
        }

        columnsInOrder.forEach((c, index) => {
            c.order = index;
        })

        return columnsInOrder;
    }

    private equalizeColumns(spreadColumns: TowerColumn[]): TowerColumn[] {
        const c_xsColumns = spreadColumns.filter(c => c.type === columnType.C_XS);
        const c_mColumns = spreadColumns.filter(c => c.type === columnType.C_M);
        const c_sColumns = spreadColumns.filter(c => c.type === columnType.C_S1S2);
        const c_xlColumns = spreadColumns.filter(c => c.type === columnType.C_XL);

        const shuffledColumns = [];
        const equalize = () => {
            if (c_xsColumns.length > 0) {
                shuffledColumns.push(c_xsColumns.pop());
            }
            if (c_mColumns.length > 0) {
                shuffledColumns.push(c_mColumns.pop());
            }
            if (c_sColumns.length > 0) {
                shuffledColumns.push(c_sColumns.pop());
            }
            if (c_xlColumns.length > 0) {
                shuffledColumns.push(c_xlColumns.pop());
            }
            if (shuffledColumns.length !== spreadColumns.length) {
                equalize();
            }
        };
        equalize();
        return shuffledColumns;
    }

    private getUnitAndAmenityDataForColumn(column: TowerColumn, offsetBasedOnColumn: number, floorHeight: number) {
        if (column) {
            column.unitsAndAmenities = [];
            const columnSegments = column.segments;
            let currentSegmentHeight = 0;
            columnSegments.forEach(segment => {
                if (segment.type === segmentType.plinth) {
                    if (!segment.custom) {
                        const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, 0);
                        column.unitsAndAmenities.push(unitsAndAmenities);
                    }
                } else if (segment.type === segmentType.middle) {
                    for (let f = 0; f < segment.floorsInSegment; f++) {
                        const height = f * floorHeight;
                        const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, currentSegmentHeight + height);
                        column.unitsAndAmenities.push(unitsAndAmenities);
                    }
                } else if (segment.type === segmentType.crown) {
                    const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, currentSegmentHeight);
                    column.unitsAndAmenities.push(unitsAndAmenities);
                }
                if (!segment.customSegmentHeight) {
                    currentSegmentHeight += segment.floorsInSegment * floorHeight;
                } else {
                    currentSegmentHeight += segment.customSegmentHeight;
                }
            });
            offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.towerBaySize;
        }
        return offsetBasedOnColumn;
    }


}